// ─── WTR Email Service (Resend via server route) ─────────────────────────────
// Sends transactional emails through server endpoint /api/resend.
//
// Frontend env vars used here:
//   REACT_APP_APP_URL=http://localhost:3000          (or your production URL)
//   REACT_APP_FROM_EMAIL=WTR <noreply@winnipegtentrentals.ca>
//
// Server-side env vars required by /api/resend:
//   RESEND_API_KEY=re_xxxxxxxxxxxx
//   FROM_EMAIL=WTR <noreply@winnipegtentrentals.ca>
//
// All send functions return { ok: true } or { ok: false, error: string }.

import { supabase } from './supabase'
import {
  availabilityPingHtml,
  crewConfirmationHtml,
  dayBeforeReminderHtml,
  quoteAcceptanceHtml,
} from './emailTemplates'

const RESEND_API = '/api/resend'

function appUrl() {
  return (process.env.REACT_APP_APP_URL || 'http://localhost:3000').replace(/\/$/, '')
}

function fromEmail() {
  return process.env.REACT_APP_FROM_EMAIL || 'Winnipeg Tent Rental <noreply@winnipegtentrentals.ca>'
}

// ─── Core send helper ─────────────────────────────────────────────────────────

async function send({ to, subject, html, jobId = null, quoteId = null, emailType }) {
  try {
    const res = await fetch(RESEND_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ from: fromEmail(), to, subject, html }),
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const errMsg = body.message || `HTTP ${res.status}`
      await logEmail({ recipient_email: to, email_type: emailType, job_id: jobId, quote_id: quoteId, status: 'failed' })
      return { ok: false, error: errMsg }
    }

    await logEmail({ recipient_email: to, email_type: emailType, job_id: jobId, quote_id: quoteId, status: 'sent' })
    return { ok: true }
  } catch (err) {
    await logEmail({ recipient_email: to, email_type: emailType, job_id: jobId, quote_id: quoteId, status: 'failed' })
    return { ok: false, error: err.message }
  }
}

async function logEmail({ recipient_email, email_type, job_id, quote_id, status }) {
  await supabase.from('email_log').insert({ recipient_email, email_type, job_id, quote_id, status })
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Send an availability ping to a staff member for a job.
 * staffMember: { id, full_name, email, response_token }
 * job: { id, event_date, setup_date, teardown_date, event_type, address,
 *         crew_required, estimated_setup_hours, estimated_teardown_hours }
 */
export async function sendAvailabilityPing(staffMember, job) {
  const token = staffMember.response_token
  const yesUrl = `${appUrl()}/staff-respond/${token}/yes`
  const noUrl  = `${appUrl()}/staff-respond/${token}/no`

  const html = availabilityPingHtml({
    staffName:     staffMember.full_name,
    jobDate:       job.event_date,
    setupDate:     job.setup_date,
    teardownDate:  job.teardown_date,
    eventType:     job.event_type,
    address:       job.address,
    crewRequired:  job.crew_required,
    setupHours:    job.estimated_setup_hours,
    teardownHours: job.estimated_teardown_hours,
    yesUrl,
    noUrl,
  })

  const eventDateFmt = job.event_date
    ? new Date(job.event_date + 'T12:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'an upcoming date'

  return send({
    to: staffMember.email,
    subject: `Are you available? — WTR Job on ${eventDateFmt}`,
    html,
    jobId: job.id,
    emailType: 'availability_ping',
  })
}

/**
 * Send a confirmation email to a staff member.
 * staffMember: { full_name, email }
 * job: { id, event_date, setup_date, teardown_date, event_type, address,
 *         estimated_setup_hours, estimated_teardown_hours, crew_required, job_notes }
 */
export async function sendCrewConfirmation(staffMember, job) {
  const html = crewConfirmationHtml({
    staffName:     staffMember.full_name,
    jobDate:       job.event_date,
    setupDate:     job.setup_date,
    teardownDate:  job.teardown_date,
    eventType:     job.event_type,
    address:       job.address,
    setupHours:    job.estimated_setup_hours,
    teardownHours: job.estimated_teardown_hours,
    crewRequired:  job.crew_required,
    jobNotes:      job.job_notes,
  })

  const eventDateFmt = job.event_date
    ? new Date(job.event_date + 'T12:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'upcoming event'

  return send({
    to: staffMember.email,
    subject: `You're confirmed! — WTR Job on ${eventDateFmt}`,
    html,
    jobId: job.id,
    emailType: 'crew_confirmation',
  })
}

/**
 * Send a day-before reminder to a single confirmed staff member.
 * Called by pg_cron or manually from the admin.
 */
export async function sendDayBeforeReminder(staffMember, job) {
  const html = dayBeforeReminderHtml({
    staffName:     staffMember.full_name,
    jobDate:       job.event_date,
    setupDate:     job.setup_date,
    eventType:     job.event_type,
    address:       job.address,
    setupHours:    job.estimated_setup_hours,
    teardownHours: job.estimated_teardown_hours,
    crewRequired:  job.crew_required,
    jobNotes:      job.job_notes,
  })

  return send({
    to: staffMember.email,
    subject: `Reminder: Job Tomorrow — Winnipeg Tent Rental`,
    html,
    jobId: job.id,
    emailType: 'day_before_reminder',
  })
}

/**
 * Send a quote acceptance link to a client.
 * quote: { id, quote_number, event_date, event_type, address,
 *           grand_total, deposit_required, acceptance_token }
 * client: { name, email }
 */
export async function sendQuoteAcceptanceLink(quote, client) {
  const token = quote.acceptance_token
  const acceptUrl  = `${appUrl()}/quote-accept/${token}?response=accept`
  const declineUrl = `${appUrl()}/quote-accept/${token}?response=decline`

  const html = quoteAcceptanceHtml({
    clientName:      client.name,
    quoteNumber:     quote.quote_number,
    eventDate:       quote.event_date,
    eventType:       quote.event_type,
    address:         quote.address,
    grandTotal:      quote.grand_total,
    depositRequired: quote.deposit_required,
    acceptUrl,
    declineUrl,
  })

  return send({
    to: client.email,
    subject: `Your Quote ${quote.quote_number} — Winnipeg Tent Rental`,
    html,
    quoteId: quote.id,
    emailType: 'quote_acceptance',
  })
}
